/* eslint-disable no-loop-func */
import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
} from "react";
import {
  ListItem,
  Typography,
  Paper,
  Box,
  Fade,
  Popper,
  makeStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  CardContent,
  Button,
  Link,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import { Close } from "mdi-material-ui";
import VesselListItem from "../Vessel/VesselListItem";
import ActionStateAndTimeListItem from "../Action/ActionStateAndTimeListItem";
import { sortLocations } from "../../utils/sorters";
import { DataStore } from "aws-amplify";
import {
  Location,
  ActionState,
  Action,
  ActionMovementType,
  PortCall,
  ActionType,
  PortCallStatus,
  VesselData,
  TemplateSubType,
  PortCallOrigin,
} from "../../models";
import { getActionArrival, getActionTime } from "../../utils/getters";
import { DataStoreContext } from "../../contexts/dataStoreContext";
import { useTranslation } from "react-i18next";
import "../../translations/i18n";
import "./CalendarPage.css";

// 3rd party imports
import {
  Eventcalendar,
  setOptions,
  localeEnGB,
  Popup,
} from "@mobiscroll/react";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";

import { useHistory } from "react-router-dom";

// Imports needed for the Modal Logic
import VesselSearchForm from "../Vessel/VesselSearchForm";
import StringKeyboardDateTimePicker from "../Common/StringKeyboardDateTimePicker";
import LocationAutocomplete from "../Location/LocationAutocomplete";
import ContactAutocomplete from "../Contact/ContactAutocomplete";
import DocumentEdit from "../Documents/DocumentEdit";
import ContactForm from "../QuickPortCall/ContactForm";
import { createPortCall } from "../QuickPortCall/create";
import { ActionTypeIds, ContactTypeIds } from "../../environment";
import {
  DataContext,
  DataContextConstants,
  DataContextOriginConstants,
} from "../../contexts/dataContext";
import { generateActionFromTemplate } from "../../utils/templateUtils";
import { isSameDateHourMinute } from "../../utils/generators";
import useInitialActionState from "../../hooks/useInitialActionState";
import useAuditMeta from "../../hooks/useAuditMeta";
import useLastPortCallByVesselId from "../../hooks/useLastPortCallByVesselId";
import useFeatureSetting from "../../hooks/useFeatureSetting";
import FeatureFlags from "../../constants/FeatureFlags";
import useTariffBooks from "../../hooks/useTariffBooks";
import { add } from "date-fns";

const useStyles = makeStyles((theme) => ({
  tooltipContainer: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[8],
    borderRadius: 4,
    border: "1px solid rgba(0,0,0,0.1)",
    maxWidth: "none",
    minWidth: "40rem",
    padding: 0,
    overflow: "hidden",
  },
  disabledTypography: {
    color: theme.palette.text.disabled,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  inputElement: {
    backgroundColor: "white",
  },
  // Modal Styles
  header: {
    height: "6rem",
    flexBasis: "auto",
    flexShrink: 0,
  },
  content: {
    flexGrow: 1,
    flexBasis: "100%",
    minHeight: "min-content",
  },
  footer: {
    height: "6rem",
    flexBasis: "auto",
    flexShrink: 0,
  },
}));

const CreatePortCallDialog = ({ open, onClose, initialData, onCreated }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();

  const [dataContext, dispatchDataContext] = useContext(DataContext);
  const dataStoreContext = useContext(DataStoreContext);

  const { getInitialActionState } = useInitialActionState();
  const auditMeta = useAuditMeta();
  const { tariffBooks } = useTariffBooks();
  const isTariffBookEnabled = useFeatureSetting(FeatureFlags.TARIFF_BOOK);

  // Constants
  const PAGE_MAIN = 0;
  const PAGE_ACTIONAGENT = 2;
  const [page, setPage] = useState(PAGE_MAIN);

  const [portCall, setPortCall] = useState(() => {
    if (!initialData) return null;

    const { arrivalTime, departureTime, locationId } = initialData;
    const actions = [];
    const template = dataStoreContext.templates.find(
      (t) =>
        t.typeId === ActionTypeIds.MOVEMENT &&
        t.subType === TemplateSubType.ACTION_ARRIVAL
    );

    // 1. Create Arrival Action
    let arrivalAction;
    if (template) {
      arrivalAction = Action.copyOf(
        generateActionFromTemplate(
          template,
          dataStoreContext,
          getInitialActionState(),
          isTariffBookEnabled
        ),
        (updated) => {
          updated.timePlanned = arrivalTime.toISOString();
          updated.auditMeta = auditMeta;
        }
      );
    } else {
      arrivalAction = new Action({
        state: getInitialActionState(),
        type: { id: ActionTypeIds.MOVEMENT },
        movementType: ActionMovementType.ARRIVAL,
        timePlanned: arrivalTime.toISOString(),
        documents: [""],
        movementPilots: [""],
        movementLinesmen: [""],
        movementMooringVessels: [""],
        auditMeta: auditMeta,
      });
    }

    // Pre-set location on arrival
    if (locationId) {
      const loc = dataStoreContext.locations.find((l) => l.id === locationId);
      if (loc) {
        arrivalAction = Action.copyOf(arrivalAction, (updated) => {
          updated.movementLocation = loc;
        });
      }
    }
    actions.push(arrivalAction);

    // 2. Create Departure Action
    let departureAction = null;
    if (departureTime) {
      const depTemplate = dataStoreContext.templates.find(
        (t) =>
          t.typeId === ActionTypeIds.MOVEMENT &&
          t.subType === TemplateSubType.ACTION_DEPARTURE
      );
      if (depTemplate) {
        departureAction = Action.copyOf(
          generateActionFromTemplate(
            depTemplate,
            dataStoreContext,
            getInitialActionState(),
            isTariffBookEnabled
          ),
          (updated) => {
            updated.timePlanned = departureTime.toISOString();
            updated.auditMeta = auditMeta;
            if (locationId) {
              const loc = dataStoreContext.locations.find(
                (l) => l.id === locationId
              );
              if (loc) updated.movementLocation = loc;
            }
          }
        );
      } else {
        departureAction = new Action({
          state: getInitialActionState(),
          type: { id: ActionTypeIds.MOVEMENT },
          movementType: ActionMovementType.DEPARTURE,
          timePlanned: departureTime.toISOString(),
          documents: [""],
          movementPilots: [""],
          movementLinesmen: [""],
          movementMooringVessels: [""],
          auditMeta: auditMeta,
        });
        if (locationId) {
          const loc = dataStoreContext.locations.find(
            (l) => l.id === locationId
          );
          if (loc) {
            departureAction = Action.copyOf(departureAction, (updated) => {
              updated.movementLocation = loc;
            });
          }
        }
      }
      actions.push(departureAction);
    }

    return new PortCall({
      status: PortCallStatus.PREARRIVAL,
      portCallTags: [""],
      documents: [""],
      vesselData: new VesselData({ name: "" }),
      actions,
      cargos: [],
      agents: [],
      origin: PortCallOrigin.PORT_CONTROLLER,
    });
  });

  const lastPortCall = useLastPortCallByVesselId(portCall?.vessel?.id);

  // Sync with last port call tags/category
  useEffect(() => {
    if (!portCall || !lastPortCall) return;
    setPortCall((prev) => {
      if (!prev) return prev;
      let newItem = prev;
      const filteredLastPortCallTags = lastPortCall?.portCallTags?.filter(
        (t) => t
      );
      if (
        lastPortCall.portCallTags &&
        JSON.stringify(filteredLastPortCallTags) !==
          JSON.stringify(prev.portCallTags)
      ) {
        newItem = PortCall.copyOf(newItem, (updated) => {
          updated.portCallTags = filteredLastPortCallTags;
        });
      }
      if (
        lastPortCall.category &&
        (!prev.category || prev.category.id !== lastPortCall.category.id)
      ) {
        newItem = PortCall.copyOf(newItem, (updated) => {
          updated.category = lastPortCall.category;
          updated.portCallCategoryId_ = lastPortCall.category.id;
        });
      }
      return newItem;
    });
  }, [portCall, setPortCall, lastPortCall]);

  const vessel = useMemo(
    () => (portCall && portCall.vessel) || null,
    [portCall]
  );

  // Use functional updates to keep handlers stable
  const setVesselData = useCallback((value) => {
    setPortCall((prev) =>
      PortCall.copyOf(prev, (updated) => {
        updated.vesselData = value;
      })
    );
  }, []);

  const setVessel = useCallback(
    (value) => {
      dispatchDataContext({
        type: DataContextConstants.SET_CREATE_PORTCALL_VESSEL,
        payload: value,
        origin: DataContextOriginConstants.CREATE,
      });
      setPortCall((prev) =>
        PortCall.copyOf(prev, (updated) => {
          updated.vessel = value;
          updated.portCallVesselId_ = value.id;
          updated.vesselData = value.vesselData;
          for (let action of updated.actions) {
            action.state = getInitialActionState(value.id);
          }
        })
      );
    },
    [dispatchDataContext, getInitialActionState]
  );

  const clearVessel = useCallback(() => {
    dispatchDataContext({
      type: DataContextConstants.SET_CREATE_PORTCALL_VESSEL,
      payload: null,
    });
    setPortCall((prev) =>
      PortCall.copyOf(prev, (updated) => {
        updated.vessel = null;
        updated.vesselData = new VesselData({ name: "" });
      })
    );
  }, [dispatchDataContext]);

  const [isUnknownVessel, setIsUnknownVessel] = useState(false);
  const [agent, setAgent] = useState(null);

  const arrival = useMemo(
    () =>
      portCall?.actions?.find(
        (a) => a.movementType === ActionMovementType.ARRIVAL
      ),
    [portCall]
  );

  const setArrival = useCallback((value) => {
    setPortCall((prev) =>
      PortCall.copyOf(prev, (updated) => {
        updated.actions = [
          ...updated.actions.filter(
            (a) => a.movementType !== ActionMovementType.ARRIVAL
          ),
          value,
        ].filter((a) => a);
      })
    );
  }, []);

  const arrivalError = useMemo(() => !arrival?.timePlanned, [arrival]);

  const departure = useMemo(
    () =>
      portCall?.actions?.find(
        (a) => a.movementType === ActionMovementType.DEPARTURE
      ),
    [portCall]
  );

  const setDeparture = useCallback((value) => {
    setPortCall((prev) =>
      PortCall.copyOf(prev, (updated) => {
        updated.actions = [
          ...updated.actions.filter(
            (a) => a.movementType !== ActionMovementType.DEPARTURE
          ),
          value,
        ].filter((a) => a);
      })
    );
  }, []);

  const departureError = useMemo(
    () =>
      departure &&
      ((!departure.timePlanned && t("PortCall.Errors.InvalidDateFormat")) ||
        ((departure.timePlanned <= arrival?.timePlanned ||
          (arrival?.timePlanned &&
            isSameDateHourMinute(
              departure.timePlanned,
              arrival.timePlanned
            ))) &&
          t("PortCall.Errors.DepartureAfterArrival"))),
    [departure, arrival, t]
  );

  const addDeparture = useCallback(() => {
    if (!arrival) return;
    const timePlanned = add(new Date(arrival.timePlanned), {
      days: 1,
    }).toISOString();
    const template = dataStoreContext.templates.find(
      (t) =>
        t.typeId === ActionTypeIds.MOVEMENT &&
        t.subType === TemplateSubType.ACTION_DEPARTURE
    );
    let newDep;
    if (template) {
      newDep = Action.copyOf(
        generateActionFromTemplate(
          template,
          dataStoreContext,
          getInitialActionState(portCall?.vessel?.id),
          isTariffBookEnabled
        ),
        (updated) => {
          updated.timePlanned = timePlanned;
          updated.auditMeta = auditMeta;
        }
      );
    } else {
      newDep = new Action({
        state: getInitialActionState(portCall?.vessel?.id),
        type: { id: ActionTypeIds.MOVEMENT },
        movementType: ActionMovementType.DEPARTURE,
        timePlanned,
        documents: [""],
        movementPilots: [""],
        movementLinesmen: [""],
        movementMooringVessels: [""],
        auditMeta: auditMeta,
      });
    }
    setDeparture(newDep);
  }, [
    setDeparture,
    arrival,
    auditMeta,
    dataStoreContext,
    getInitialActionState,
    portCall,
    isTariffBookEnabled,
  ]);

  const clearDeparture = useCallback(() => {
    setDeparture(null);
  }, [setDeparture]);

  const location = useMemo(
    () => (arrival && arrival.movementLocation) || null,
    [arrival]
  );
  const setLocation = useCallback(
    (value) => {
      if (arrival) {
        setArrival(
          Action.copyOf(arrival, (updated) => {
            updated.movementLocation = value;
          })
        );
      }
    },
    [arrival, setArrival]
  );

  const handleCreate = async (moreOptions) => {
    dispatchDataContext({
      type: DataContextConstants.SET_CREATE_PORTCALL_VESSEL,
      payload: null,
    });
    await createPortCall(
      portCall,
      location,
      arrival,
      departure,
      agent,
      [],
      auditMeta,
      isTariffBookEnabled,
      dataStoreContext,
      tariffBooks
    );

    // Trigger refresh in parent
    if (onCreated) {
      onCreated();
    }

    if (moreOptions) {
      history.push(`/port-call/${portCall.id}/`);
    } else {
      onClose();
    }
  };

  const handleUploadedDocuments = (value) => {
    setArrival(
      Action.copyOf(arrival, (updated) => {
        updated.documents = value;
      })
    );
  };

  const disabled = useMemo(
    () =>
      !portCall ||
      (isUnknownVessel
        ? Boolean(!portCall.vesselData.name)
        : Boolean(!vessel)) ||
      Boolean(arrivalError) ||
      Boolean(departureError),
    [portCall, isUnknownVessel, vessel, arrivalError, departureError]
  );

  useEffect(() => {
    if (
      dataContext.createPortCallVessel &&
      dataContext.origin !== DataContextOriginConstants.UPDATE &&
      (!vessel || vessel.id !== dataContext.createPortCallVessel.id)
    ) {
      setVessel(dataContext.createPortCallVessel);
    }
  }, [dataContext, vessel, setVessel]);

  useEffect(() => {
    if (dataContext.pendingCreateVessel) {
      const origin = dataContext.origin;
      dispatchDataContext({
        type: DataContextConstants.SET_PENDING_CREATE_VESSEL,
        payload: null,
        origin: DataContextOriginConstants.CREATE,
      });
      dispatchDataContext({
        type: DataContextConstants.SET_CREATE_PORTCALL_VESSEL,
        payload: dataContext.pendingCreateVessel,
        origin,
      });
    }
  }, [dataContext, dispatchDataContext]);

  useEffect(() => {
    return () => {
      dispatchDataContext({
        type: DataContextConstants.SET_CREATE_PORTCALL_VESSEL,
        payload: null,
        origin: DataContextOriginConstants.CREATE,
      });
    };
  }, [dispatchDataContext]);

  if (!open || !portCall) return null;

  if (page === PAGE_ACTIONAGENT)
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <ContactForm
            contact={agent}
            contactType={dataStoreContext.contactTypes.find(
              (ct) => ct.id === ContactTypeIds.AGENT
            )}
            onChange={(value) => setAgent(value)}
            showTitle
            onBack={() => setPage(PAGE_MAIN)}
          />
        </DialogContent>
      </Dialog>
    );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box
        display="flex"
        overflow="hidden auto"
        width="100%"
        id={"QuickPortCall"}
      >
        <Grid container wrap="nowrap" direction="column">
          <Grid
            item
            container
            justifyContent="center"
            alignItems="center"
            className={classes.header}
          >
            <Typography variant="h2">
              {t("PortCall.Labels.NewPortCallTitle")}
            </Typography>
            <IconButton
              onClick={onClose}
              style={{ marginLeft: -59, position: "relative", left: 96 }}
            >
              <Close fontSize="large" />
            </IconButton>
          </Grid>
          <Grid
            item
            className={classes.content}
            container
            justifyContent="center"
          >
            <Box width="100%">
              <CardContent
                style={{ maxWidth: "60rem", margin: "0 auto", paddingTop: 0 }}
              >
                <VesselSearchForm
                  vessel={vessel}
                  setVessel={setVessel}
                  clearVessel={clearVessel}
                  vesselData={portCall.vesselData}
                  setVesselData={setVesselData}
                  isUnknownVessel={isUnknownVessel}
                  setIsUnknownVessel={setIsUnknownVessel}
                  certificateFrom={arrival?.timePlanned}
                  certificateTo={
                    departure?.timePlanned || new Date().toISOString()
                  }
                  allowCreateVessel
                />
              </CardContent>
              <CardContent
                style={{ paddingTop: 0, maxWidth: "60rem", margin: "0 auto" }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-end"
                >
                  <Typography variant="h5">
                    {t("PortCall.Labels.Schedule")}
                  </Typography>
                  {!agent && (
                    <Link
                      onClick={() => setPage(PAGE_ACTIONAGENT)}
                      id="NavCreateAgentButton"
                    >
                      <Typography>{t("Agent.Buttons.CreateAgent")}</Typography>
                    </Link>
                  )}
                </Box>
                <Box display="flex" height="fit-content">
                  <StringKeyboardDateTimePicker
                    warningPastDate={true}
                    id="ArrivalTimePlannedInput"
                    label={t("PortCall.Labels.PlannedTimeOfArrival")}
                    style={{
                      width: "100%",
                      marginTop: "0.25rem",
                      marginBottom: "1rem",
                    }}
                    value={arrival?.timePlanned}
                    onChange={(value) =>
                      setArrival(
                        Action.copyOf(arrival, (updated) => {
                          updated.timePlanned = value;
                        })
                      )
                    }
                    okLabel={t("Common.Buttons.Confirm")}
                  />
                  <LocationAutocomplete
                    id="ArrivalLocationInput"
                    style={{
                      width: "100%",
                      marginLeft: "1rem",
                      marginRight: "1rem",
                    }}
                    value={location}
                    onChange={(value) => setLocation(value)}
                    label={t("PortCall.Labels.ArrivalLocation")}
                    helperText={
                      location?.dockable === false
                        ? t("ActionScheduleEdit.Labels.NotBerthableLocation")
                        : ""
                    }
                  />
                  <ContactAutocomplete
                    id="CharteredAgentInput"
                    style={{ width: "100%" }}
                    value={agent}
                    onChange={(value) => setAgent(value)}
                    label={t("Agent.Labels.CharterAgent")}
                    filter={(c) => c.type?.id === ContactTypeIds.AGENT}
                  />
                </Box>
                <Box
                  id="ArrivalAttachmentsContainer"
                  style={{
                    paddingTop: 0,
                    paddingBottom: "1rem",
                    maxWidth: "60rem",
                    margin: "0 auto",
                  }}
                >
                  <DocumentEdit
                    filePath={`portcalls/${portCall.id}/actions/${arrival?.id}/`}
                    portCallId={portCall.id}
                    objId={arrival?.id}
                    objDocuments={arrival?.documents?.filter((d) => d) ?? []}
                    onChange={handleUploadedDocuments}
                    saveDisabled
                    disableDownload
                    hideEmptyMessage
                  />
                </Box>

                <Box display="flex" height="5rem" alignItems="center">
                  {departure ? (
                    <>
                      <StringKeyboardDateTimePicker
                        width="calc(33.333% - 0.666rem)"
                        warningPastDate={true}
                        id="DepartureTimePlannedInput"
                        label={t("PortCall.Labels.PlannedTimeOfDeparture")}
                        style={{
                          width: "100%",
                          marginTop: "1.25rem",
                          marginBottom: "1rem",
                        }}
                        value={departure.timePlanned}
                        onChange={(value) =>
                          setDeparture(
                            Action.copyOf(departure, (updated) => {
                              updated.timePlanned = value;
                            })
                          )
                        }
                        error={Boolean(departureError)}
                        helperText={departureError}
                        okLabel={t("Common.Buttons.Confirm")}
                      />
                      <Grid item style={{ margin: ".25rem 0 1rem 1rem" }}>
                        <IconButton onClick={clearDeparture}>
                          <Close />
                        </IconButton>
                      </Grid>
                    </>
                  ) : (
                    <Button
                      id="AddDepartureButton"
                      color="primary"
                      variant="outlined"
                      onClick={addDeparture}
                      disabled={Boolean(arrivalError)}
                    >
                      <Typography>
                        {t("PortCall.Buttons.AddDeparture")}
                      </Typography>
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            container
            justifyContent="center"
            alignItems="center"
            className={classes.footer}
          >
            <Button
              color="primary"
              size="large"
              disabled={disabled}
              onClick={() => handleCreate(true)}
              id="CreatePortCallMoreOptionsButton"
            >
              <Typography>{t("PortCall.Buttons.MoreOptions")}</Typography>
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={disabled}
              style={{ margin: "1rem" }}
              onClick={() => handleCreate(false)}
              id="CreatePortCallButton"
            >
              {t("Common.Buttons.Create")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

// --- Main Component: CalendarPage ---
const CalendarPage = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredEvent, setHoveredEvent] = useState(null);

  // Initialize selectedPort from localStorage or default to 0
  const [selectedPort, setSelectedPort] = useState(() => {
    const saved = localStorage.getItem("calendarPageSelectedPort");
    if (saved === null) return 0;
    // If the saved value is "0", cast it to a number because the "All Ports" option uses ID 0 (number)
    // while actual Port IDs are UUID strings.
    if (saved === "0") return 0;
    return saved;
  });

  // Save selectedPort to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("calendarPageSelectedPort", selectedPort);
  }, [selectedPort]);

  const [filterList, setFilterList] = useState([]);

  // -- Trigger state to force refresh
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Modal State
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalData, setCreateModalData] = useState(null);

  // --- Highlight & Tooltip State ---
  const [hoverDate, setHoverDate] = useState(null);
  const [hoverResource, setHoverResource] = useState(null); // Added state
  const [tooltipAnchor, setTooltipAnchor] = useState(null); // Added state for tooltip anchor
  const [isTooltipOpen, setIsTooltipOpen] = useState(false); // Added state for tooltip open/close
  const [displayedResources, setDisplayedResources] = useState([]);

  // --- Show Remarks Toggle State ---
  // Initialize showRemarks from localStorage or default to false
  const [showRemarks, setShowRemarks] = useState(() => {
    const saved = localStorage.getItem("calendarPageShowRemarks");
    return saved === "true";
  });

  // Save showRemarks to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("calendarPageShowRemarks", String(showRemarks));
  }, [showRemarks]);

  const handleEventHoverIn = useCallback((args) => {
    // Only show tooltip for real events that have our custom data
    if (args.event && args.event.portCallId) {
      setHoveredEvent(args.event);
      setAnchorEl(args.domEvent.target);
    }
  }, []);

  const handleEventHoverOut = useCallback(() => {
    setAnchorEl(null);
    setHoveredEvent(null);
  }, []);

  const handleChange = (event) => {
    setSelectedPort(event.target.value);
  };

  const theme = useTheme();
  const classes = useStyles();

  const [portCallsMerged, setPortCallsMerged] = useState([]);

  const { t } = useTranslation();

  setOptions({
    locale: localeEnGB,
    theme: "ios",
    themeVariant: "light",
  });

  const myView = useMemo(
    () => ({
      timeline: {
        type: "week",
        maxEventStack: 1,
        eventHeight: "variable", // Always variable to allow growth
        rowHeight: "variable", // Always variable, min-height handled via CSS
      },
    }),
    []
  );

  const { locations: initialLocations } = useContext(DataStoreContext);
  const locations = useMemo(
    () => initialLocations.filter((l) => l.allocatable).sort(sortLocations),
    [initialLocations]
  );

  const myResources = useMemo(
    () =>
      locations
        .filter((location) => {
          if (location.type !== "BERTH") return false;
          if (selectedPort === 0) return true;
          return location.parent.locationParentId === selectedPort;
        })
        .map((location) => {
          return {
            id: location.id,
            name: (location.parentName || "") + (location.name || ""),
            originalLocationData: location,
          };
        }),
    [locations, selectedPort]
  );

  // --- Sync derived resources to display state ---
  useEffect(() => {
    setDisplayedResources(myResources);
  }, [myResources]);

  // --- Highlighting Handlers ---
  const handleCellHoverIn = useCallback((args) => {
    setDisplayedResources((prevResources) => {
      const updated = prevResources.map((r) => ({
        ...r,
        cssClass: r.id === args.resource.id ? "mds-highlight-row-hover" : "",
      }));
      // Set the hover resource for the tooltip name
      setHoverResource(updated.find((r) => r.id === args.resource.id));
      return updated;
    });

    setHoverDate(args.date);
    setTooltipAnchor(args.domEvent.target);
    setIsTooltipOpen(true);
  }, []);

  const handleCellHoverOut = useCallback(() => {
    setHoverDate(null);
    setHoverResource(null);
    setDisplayedResources((prevResources) =>
      prevResources.map((r) => ({ ...r, cssClass: "" }))
    );
    setTooltipAnchor(null);
    setIsTooltipOpen(false);
  }, []);

  const renderCell = useCallback(
    (args) => {
      const isHover = hoverDate && args.date.getTime() === hoverDate.getTime();
      return isHover ? <div className="mds-highlight-col-hover" /> : null;
    },
    [hoverDate]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let page = 0;
      let limit = 500;
      let actions = [];
      do {
        if (cancelled) return;
        const portCalls = await DataStore.query(PortCall, (c) =>
          c
            .status("ne", PortCallStatus.DELETED)
            .status("ne", PortCallStatus.CANCELLED)
        );
        const data = await DataStore.query(
          Action,
          (c) =>
            c
              .or((c) =>
                portCalls.reduce((c, p) => c.actionPortCallId_("eq", p.id), c)
              )
              .movementType("ne", null)
              .state("ne", ActionState.CANCELLED)
              .state("ne", ActionState.DELETED)
              .state("ne", ActionState.REQUESTED),
          {
            limit: limit,
            page: page,
            sort: (c) => c.timePlanned("DESCENDING"),
          }
        );
        if (cancelled) return;

        actions = [...actions, ...data];
        actions = actions.map((action) => {
          if (!action.actionMovementLocationId) return action;
          return Action.copyOf(action, (updated) => {
            updated.movementLocation = locations.find(
              (el) => el.id === action.actionMovementLocationId
            );
          });
        });
        setPortCallsMerged((prev) => {
          return [
            ...prev,
            ...data
              .filter((a) => a.movementType === ActionMovementType.ARRIVAL)
              .map((arrival) =>
                PortCall.copyOf(arrival.portCall, (updated) => {
                  updated.actions = actions.filter(
                    (a) => a.portCall && a.portCall.id === updated.id
                  );
                })
              ),
          ];
        });
        page = data.length < limit ? null : ++page;
      } while (page);
    })();

    const subscription = DataStore.observe(Action).subscribe(async (msg) => {
      const { element, opType } = msg;
      let actionDeleted =
        opType === "DELETE" || element?.state === ActionState.DELETED;
      let item;
      setPortCallsMerged((prev) => {
        const portCall = prev.find((p) => p.id === element.actionPortCallId_);
        item = portCall && portCall.actions.find((i) => i.id === element.id);
        return prev;
      });
      if (item && item._version >= element._version) {
        return;
      }

      if (!actionDeleted) {
        const type = element.actionTypeId
          ? await DataStore.query(ActionType, element.actionTypeId)
          : null;
        const movementLocation = element.actionMovementLocationId
          ? await DataStore.query(Location, element.actionMovementLocationId)
          : null;
        if (cancelled) return;

        setPortCallsMerged((prev) =>
          prev.map((p) =>
            p.id === element.actionPortCallId_
              ? PortCall.copyOf(p, (updated) => {
                  updated.actions = [
                    ...updated.actions.filter((a) => a.id !== element.id),
                    Action.copyOf(element, (updated) => {
                      updated.type = type;
                      updated.movementLocation = movementLocation;
                    }),
                  ];
                })
              : p
          )
        );
      } else {
        setPortCallsMerged((prev) =>
          prev.map((p) =>
            p.id === element.actionPortCallId_
              ? PortCall.copyOf(p, (updated) => {
                  updated.actions = updated.actions.filter(
                    (a) => a.id !== element.id
                  );
                })
              : p
          )
        );
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [setPortCallsMerged, locations, refreshTrigger]);

  useEffect(() => {
    const uniquePorts = [
      ...new Set(
        locations
          .filter((location) => location.type === "PORT")
          .map((location) => {
            return { id: location.id, name: location.name };
          })
      ),
    ];
    setFilterList([
      { id: 0, name: t("Filter.Labels.AllPorts") },
      ...uniquePorts,
    ]);
  }, [locations, t]);

  const myCustomResource = useCallback(
    (resource) => {
      const { parentName, name } = resource.originalLocationData;
      return (
        <>
          {parentName && (
            <Typography
              variant="caption"
              className={classes.disabledTypography}
            >
              {parentName}
            </Typography>
          )}
          <Typography variant="body1">{name}</Typography>
        </>
      );
    },
    [classes.disabledTypography]
  );

  const [rawEvents, setRawEvents] = useState([]);

  useEffect(() => {
    const newRawEvents = [];

    portCallsMerged.forEach((portCall) => {
      const actions = portCall?.actions;
      const arrival = getActionArrival(actions);
      if (!actions || !arrival) return;

      const movements = actions
        .filter((a) =>
          ["ARRIVAL", "DEPARTURE", "SHIFT_ARRIVAL", "SHIFT_DEPARTURE"].includes(
            a.movementType
          )
        )
        .sort(
          (a, b) => new Date(getActionTime(a)) - new Date(getActionTime(b))
        );

      let currentStart = arrival;
      let currentBerth = arrival.movementLocation;

      const pushEvent = (endAction) => {
        const start = new Date(getActionTime(currentStart));
        const end = endAction
          ? new Date(getActionTime(endAction))
          : new Date(start.getTime() + 365 * 24 * 60 * 60 * 1000);

        if (end <= start) return;

        const isShiftStart = ["SHIFT_ARRIVAL", "SHIFT_DEPARTURE"].includes(
          currentStart.movementType
        );
        const isShiftEnd =
          endAction &&
          ["SHIFT_ARRIVAL", "SHIFT_DEPARTURE"].includes(endAction.movementType);
        const isStartNotPlanned = currentStart.state !== ActionState.PLANNED;
        const isEndNotPlanned =
          endAction && endAction.state !== ActionState.PLANNED;
        const isLocked =
          isShiftStart || isShiftEnd || isStartNotPlanned || isEndNotPlanned;

        // Logic for title based on showRemarks toggle
        const vesselName = portCall.vesselData?.name || "Vessel";
        let title = vesselName;
        if (showRemarks && portCall.remarks) {
          title = `${vesselName}\nRemarks: ${portCall.remarks}`;
        }

        const eventData = {
          start,
          end,
          title: title,
          resource: currentBerth?.id || "not-allocated",
          portCallId: portCall.id,
          vesselData: portCall.vesselData,
          resourceLocation: currentBerth,
          fromAction: currentStart,
          toAction: endAction,
          status: portCall.status,
          portCall,
        };

        if (isLocked) {
          newRawEvents.push({
            ...eventData,
            dragBetweenResources: false,
            dragBetweenSlots: false,
            dragInTime: false,
            resize: false,
            color:
              portCall.status === "CLOSED"
                ? theme.palette.grey[500]
                : theme.palette.primary.dark,
          });
        } else {
          newRawEvents.push({
            ...eventData,
            color: "#311b92",
          });
        }
      };

      for (let i = 0; i < movements.length; i++) {
        const action = movements[i];
        if (action.id === currentStart?.id) continue;

        if (["DEPARTURE", "SHIFT_DEPARTURE"].includes(action.movementType)) {
          pushEvent(action);

          if (action.movementType === "SHIFT_DEPARTURE") {
            const next = movements[i + 1];
            if (next?.movementType === "SHIFT_ARRIVAL") {
              currentStart = next;
              currentBerth = next.movementLocation;
              i++;
            } else {
              currentStart = null;
            }
          } else {
            currentStart = null;
          }
        }
      }

      if (currentStart) {
        pushEvent(null);
      }
    });

    setRawEvents(newRawEvents);
  }, [portCallsMerged, theme, showRemarks]);

  const history = useHistory();

  const handleEventClick = useCallback(
    (eventData) => {
      history.push(`/port-call/${eventData.event.portCallId}`);
    },
    [history]
  );

  const handleEventUpdate = useCallback(async (eventData) => {
    const { event: updatedEvent, oldResource, resource } = eventData;
    const {
      start: newStartTime,
      end: newEndTime,
      fromAction,
      toAction,
    } = updatedEvent;

    try {
      const arrivalId = fromAction?.id;
      const departureId = toAction?.id;

      const arrivalAction = arrivalId
        ? await DataStore.query(Action, arrivalId)
        : null;
      const departureAction = departureId
        ? await DataStore.query(Action, departureId)
        : null;

      let newLocation = null;
      if (resource !== oldResource) {
        newLocation = await DataStore.query(Location, resource);
      }

      if (arrivalAction && arrivalAction.state === ActionState.PLANNED) {
        await DataStore.save(
          Action.copyOf(arrivalAction, (updated) => {
            updated.timePlanned = newStartTime.toISOString();
            if (newLocation) {
              updated.movementLocation = newLocation;
            }
          })
        );
      }

      if (departureAction && departureAction.state === ActionState.PLANNED) {
        await DataStore.save(
          Action.copyOf(departureAction, (updated) => {
            updated.timePlanned = newEndTime.toISOString();
            if (newLocation) {
              updated.movementLocation = newLocation;
            }
          })
        );
      }
    } catch (error) {
      console.error("Error updating event via calendar:", error);
    }
  }, []);

  const handleEventCreate = useCallback((eventData) => {
    const newEvent = eventData.event;
    setCreateModalData({
      arrivalTime: newEvent.start,
      departureTime: newEvent.end,
      locationId: newEvent.resource,
    });
    setCreateModalOpen(true);
    return false;
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      style={{ backgroundColor: "#f7f7f7", paddingTop: "1rem" }}
    >
      {createModalOpen && (
        <CreatePortCallDialog
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          initialData={createModalData}
          onCreated={() => setRefreshTrigger((prev) => prev + 1)}
        />
      )}
      <Box
        display="flex"
        flexDirection="row"
        justifyContent={"flex-end"}
        marginRight="1rem"
        alignItems={"end"}
      >
        <Box display="flex" alignItems="center" marginRight="1rem">
          <FormControlLabel
            control={
              <Switch
                checked={showRemarks}
                onChange={(e) => setShowRemarks(e.target.checked)}
                color="primary"
              />
            }
            label={t("Calendar.Labels.ShowRemarks")}
          />
        </Box>
        <Box width={"300px"}>
          <FormControl className={classes.formControl} fullWidth>
            <InputLabel id="selectFilterLabel">
              {t("Filter.Labels.SelectPort")}
            </InputLabel>
            <Select
              labelId="selectFilterLabel"
              className={classes.inputElement}
              id="selectFilter"
              value={selectedPort}
              onChange={handleChange}
            >
              {filterList.map((filter, index) => {
                return (
                  <MenuItem key={filter.id} value={filter.id}>
                    {filter.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box height="100%">
        <Eventcalendar
          className={`${
            showRemarks ? "mbsc-event-wrapping" : ""
          } mds-highlight-hover`}
          clickToCreate={true}
          dragToCreate={true}
          dragToMove={true}
          dragToResize={true}
          eventDelete={false}
          view={myView}
          data={rawEvents}
          resources={displayedResources}
          renderResource={myCustomResource}
          renderCell={renderCell}
          showEventTooltip={false}
          onCellHoverIn={handleCellHoverIn}
          onCellHoverOut={handleCellHoverOut}
          onEventHoverIn={handleEventHoverIn}
          onEventHoverOut={handleEventHoverOut}
          onEventClick={handleEventClick}
          onEventUpdate={handleEventUpdate}
          onEventCreate={handleEventCreate}
        />
        <Popup
          anchor={tooltipAnchor}
          closeOnScroll={true}
          display="anchored"
          focusOnClose={false}
          isOpen={isTooltipOpen}
          scrollLock={false}
          showOverlay={false}
        >
          {hoverResource && hoverDate && (
            <Box
              className="mds-highlight-tooltip"
              style={{ pointerEvents: "none" }}
            >
              <Box
                className="mds-highlight-tooltip-name"
                style={{ fontWeight: "600" }}
              >
                <Typography
                  variant="caption"
                  className={classes.disabledTypography}
                >
                  {hoverResource.originalLocationData.parentName}
                </Typography>
                <Typography variant="body1">
                  {hoverResource.originalLocationData.name}
                </Typography>
              </Box>
            </Box>
          )}
        </Popup>
        <Popper
          open={Boolean(anchorEl) && Boolean(hoveredEvent)}
          anchorEl={anchorEl}
          placement="bottom-start"
          transition
          style={{ zIndex: 2147483647, pointerEvents: "none" }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Paper className={classes.tooltipContainer}>
                {hoveredEvent && (
                  <>
                    <ListItem style={{ padding: 0 }}>
                      <Box width="16rem">
                        <VesselListItem vesselData={hoveredEvent.vesselData} />
                      </Box>
                      <Box width="10rem">
                        {hoveredEvent.fromAction && (
                          <ActionStateAndTimeListItem
                            action={hoveredEvent.fromAction}
                            disableTooltip
                          />
                        )}
                      </Box>
                      <Box width="10rem">
                        {hoveredEvent.toAction ? (
                          <ActionStateAndTimeListItem
                            action={hoveredEvent.toAction}
                            disableTooltip
                          />
                        ) : (
                          <Box p={2}>
                            <Typography className={classes.disabledTypography}>
                              {t("LocationTimeline.Labels.NoUpcomingAction")}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                  </>
                )}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </Box>
  );
};

export default CalendarPage;